import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './CustomFormBuilder.css';

const FIELD_TYPES = [
  { id: 'text', label: 'Text', emoji: '📝', icon: 'A' },
  { id: 'number', label: 'Number', emoji: '🔢', icon: '1' },
  { id: 'scale', label: 'Scale (1-10)', emoji: '📊', icon: '1-10' },
  { id: 'dropdown', label: 'Dropdown', emoji: '▼', icon: 'v' },
  { id: 'checkbox', label: 'Checkbox', emoji: '☑️', icon: '✓' },
  { id: 'date', label: 'Date', emoji: '📅', icon: 'D' },
];

function CustomFormBuilder({ onClose, onFormCreated }) {
  const toast = useToast();
  const navigate = useNavigate();
  // Used as a modal (with onClose) or a full route (no onClose → navigate back).
  const handleClose = onClose || (() => navigate(-1));
  const [step, setStep] = useState(1); // 1: form info, 2: fields, 3: review
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formType, setFormType] = useState('metric');
  const [fields, setFields] = useState([]);
  const [currentField, setCurrentField] = useState(null);
  const [loading, setLoading] = useState(false);

  const addField = () => {
    const newField = {
      id: Date.now(),
      field_name: '',
      field_type: 'text',
      field_label: '',
      placeholder_text: '',
      is_required: true,
      options: [],
      min_value: null,
      max_value: null,
      unit: '',
      help_text: ''
    };
    setFields([...fields, newField]);
    setCurrentField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    setFields(fields.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  };

  const removeField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (currentField === fieldId) {
      setCurrentField(fields[0]?.id || null);
    }
  };

  const handleCreateForm = async () => {
    if (!formName.trim()) {
      toast('Please enter a form name', 'warning');
      return;
    }

    if (fields.length === 0) {
      toast('Please add at least one field', 'warning');
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${API_BASE_URL}/api/v1/wellness/custom-forms`,
        {
          form_name: formName,
          description: formDesc,
          form_type: formType,
          fields: fields
        },
        { headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem('user') || '{}').token}` } }
      );

      toast('✅ Form created successfully!', 'success');
      onFormCreated && onFormCreated(res.data);
      handleClose();
    } catch (err) {
      console.error('Error creating form:', err);
      toast('Error creating form', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="builder-modal">
      <div className="builder-overlay" onClick={handleClose} />

      <div className="builder-content">
        <div className="builder-header">
          <h2>🏗️ Custom Form Builder</h2>
          <span className="step-indicator">Step {step} of 3</span>
          <button className="btn-close" onClick={handleClose}>✕</button>
        </div>

        <div className="builder-body">
          {step === 1 && (
            <div className="step-content">
              <h3>Step 1: Form Information</h3>

              <div className="form-group">
                <label>Form Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Jump Height Test, Flexibility Assessment"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="What is this form for? When should athletes fill it out?"
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Form Type</label>
                <div className="type-selector">
                  {['metric', 'assessment', 'questionnaire'].map(type => (
                    <button
                      key={type}
                      type="button"
                      className={`type-btn ${formType === type ? 'active' : ''}`}
                      onClick={() => setFormType(type)}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-examples">
                <h4>Examples:</h4>
                <p><strong>Metric:</strong> Jump height, sit-ups, flexibility measurements</p>
                <p><strong>Assessment:</strong> Skills checklist, readiness evaluation</p>
                <p><strong>Questionnaire:</strong> Subjective wellness, injury screening</p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <h3>Step 2: Add Form Fields</h3>

              <div className="fields-container">
                <div className="fields-list">
                  <div className="field-types">
                    <h4>Available Fields</h4>
                    {FIELD_TYPES.map(type => (
                      <button
                        key={type.id}
                        type="button"
                        className="field-type-btn"
                        onClick={() => {
                          const newField = {
                            id: Date.now(),
                            field_name: '',
                            field_type: type.id,
                            field_label: '',
                            placeholder_text: '',
                            is_required: true,
                            options: [],
                            unit: '',
                            help_text: ''
                          };
                          setFields([...fields, newField]);
                          setCurrentField(newField.id);
                        }}
                      >
                        <span className="emoji">{type.emoji}</span>
                        <span>{type.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="added-fields">
                    <h4>Added Fields ({fields.length})</h4>
                    <div className="fields-added-list">
                      {fields.length === 0 ? (
                        <p className="no-fields">Click above to add fields</p>
                      ) : (
                        fields.map((field, idx) => (
                          <div
                            key={field.id}
                            className={`field-item ${currentField === field.id ? 'active' : ''}`}
                            onClick={() => setCurrentField(field.id)}
                          >
                            <span className="field-order">{idx + 1}</span>
                            <span className="field-preview">
                              {field.field_label || `Field ${idx + 1}`}
                              <br />
                              <small>{field.field_type}</small>
                            </span>
                            <button
                              className="btn-delete-field"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeField(field.id);
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Field Editor */}
                {currentField && (
                  <div className="field-editor">
                    <h4>Edit Field</h4>
                    {(() => {
                      const field = fields.find(f => f.id === currentField);
                      if (!field) return null;

                      return (
                        <div className="editor-form">
                          <div className="form-group">
                            <label>Field Label *</label>
                            <input
                              type="text"
                              placeholder="What to ask? (e.g., 'Jump Height in cm')"
                              value={field.field_label}
                              onChange={(e) => updateField(field.id, { field_label: e.target.value })}
                            />
                          </div>

                          <div className="form-group">
                            <label>Placeholder Text</label>
                            <input
                              type="text"
                              placeholder="e.g., 'Enter height...'"
                              value={field.placeholder_text}
                              onChange={(e) => updateField(field.id, { placeholder_text: e.target.value })}
                            />
                          </div>

                          {(field.field_type === 'number' || field.field_type === 'scale') && (
                            <>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Min Value</label>
                                  <input
                                    type="number"
                                    value={field.min_value || 0}
                                    onChange={(e) => updateField(field.id, { min_value: parseFloat(e.target.value) })}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Max Value</label>
                                  <input
                                    type="number"
                                    value={field.max_value || 100}
                                    onChange={(e) => updateField(field.id, { max_value: parseFloat(e.target.value) })}
                                  />
                                </div>
                              </div>

                              <div className="form-group">
                                <label>Unit (e.g., cm, kg, %)</label>
                                <input
                                  type="text"
                                  placeholder="Leave blank if none"
                                  value={field.unit}
                                  onChange={(e) => updateField(field.id, { unit: e.target.value })}
                                />
                              </div>
                            </>
                          )}

                          {(field.field_type === 'dropdown' || field.field_type === 'checkbox') && (
                            <div className="form-group">
                              <label>Options (one per line)</label>
                              <textarea
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                                value={field.options.join('\n')}
                                onChange={(e) => updateField(field.id, { options: e.target.value.split('\n').filter(o => o.trim()) })}
                                rows="4"
                              />
                            </div>
                          )}

                          <div className="form-group">
                            <label>Help Text</label>
                            <input
                              type="text"
                              placeholder="Additional guidance for athletes"
                              value={field.help_text}
                              onChange={(e) => updateField(field.id, { help_text: e.target.value })}
                            />
                          </div>

                          <div className="form-group checkbox">
                            <input
                              type="checkbox"
                              id="required"
                              checked={field.is_required}
                              onChange={(e) => updateField(field.id, { is_required: e.target.checked })}
                            />
                            <label htmlFor="required">Required field</label>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content">
              <h3>Step 3: Review & Create</h3>

              <div className="review-section">
                <div className="review-item">
                  <strong>Form Name:</strong> {formName}
                </div>
                <div className="review-item">
                  <strong>Type:</strong> {formType.charAt(0).toUpperCase() + formType.slice(1)}
                </div>
                <div className="review-item">
                  <strong>Fields:</strong> {fields.length}
                </div>

                <div className="field-preview-list">
                  <h4>Fields Preview:</h4>
                  {fields.map((field, idx) => (
                    <div key={field.id} className="preview-field">
                      <span className="preview-num">{idx + 1}</span>
                      <div className="preview-content">
                        <strong>{field.field_label || 'Untitled'}</strong>
                        <small>Type: {field.field_type}{field.unit ? ` (${field.unit})` : ''}{!field.is_required ? ' (Optional)' : ''}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="builder-footer">
          <button
            className="btn-secondary"
            onClick={() => step > 1 ? setStep(step - 1) : handleClose()}
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>

          {step < 3 ? (
            <button
              className="btn-primary"
              onClick={() => {
                if (step === 1 && !formName.trim()) {
                  toast('Please enter a form name', 'warning');
                  return;
                }
                if (step === 2 && fields.length === 0) {
                  toast('Please add at least one field', 'warning');
                  return;
                }
                setStep(step + 1);
              }}
            >
              Next →
            </button>
          ) : (
            <button
              className="btn-create"
              onClick={handleCreateForm}
              disabled={loading}
            >
              {loading ? '🏗️ Creating...' : '✅ Create Form'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomFormBuilder;
